import { httpClient } from '@/shared/api/httpClient';
import { endpoints } from '@/shared/api/endpoints';

/** A field entry from create-fields, describing one CMS-configured profile field. */
export type TeamFieldValidation = {
  minLength?: number;
  maxLength?: number;
  min?: number;
  max?: number;
  pattern?: string;
};

export type TeamRoleField = {
  _id?: string;
  visible?: boolean;
  required?: boolean;
  fieldId?: {
    fieldName?: string;
    fieldLabel?: string;
    fieldType?: string;
    placeholder?: string;
    validation?: TeamFieldValidation;
  };
};

export type TeamMember = {
  _id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  mobileNumber?: string;
  mobileCountryCode?: string;
  status?: string;
  createdAt?: string;
  updatedAt?: string;
  [key: string]: unknown;
};

export type AssignedClient = {
  _id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  companyName?: string;
  status?: string;
  [key: string]: unknown;
};

export async function fetchTeamCreateFields() {
  const { data } = await httpClient.get(endpoints.team.createFields);
  return data;
}

export async function createTeamMember(payload: Record<string, unknown>) {
  const { data } = await httpClient.post(endpoints.team.create, payload);
  return data;
}

export async function fetchTeamMembers(params: Record<string, unknown> = {}) {
  const { data } = await httpClient.get(endpoints.team.list, { params });
  return data;
}

export async function fetchTeamMemberById(memberId: string) {
  const { data } = await httpClient.get(endpoints.team.detail(memberId));
  return data;
}

export async function updateTeamMember(
  memberId: string,
  payload: Record<string, unknown>,
) {
  const { data } = await httpClient.patch(endpoints.team.update(memberId), payload);
  return data;
}

export async function deleteTeamMember(memberId: string) {
  const { data } = await httpClient.delete(endpoints.team.delete(memberId));
  return data;
}

export async function fetchAssignedClients(memberId: string) {
  const { data } = await httpClient.get(endpoints.team.assignedClients(memberId));
  return data;
}

/** Logged-in team member (or agency) — clients they can run campaigns for. */
export async function fetchMyAssignedClients(
  params: Record<string, unknown> = {},
) {
  const { data } = await httpClient.get(endpoints.team.myAssignedClients, {
    params,
  });
  return data;
}

export async function assignClientsToMember(
  memberId: string,
  clientIds: string[],
) {
  const { data } = await httpClient.post(endpoints.team.assignClients(memberId), {
    clientIds,
  });
  return data;
}

export async function unassignClientsFromMember(
  memberId: string,
  clientIds: string[],
) {
  const { data } = await httpClient.post(
    endpoints.team.unassignClients(memberId),
    { clientIds },
  );
  return data;
}

export async function unassignAllClientsFromMember(memberId: string) {
  const { data } = await httpClient.post(
    endpoints.team.unassignAllClients(memberId),
  );
  return data;
}
